import { http, HttpResponse } from "msw";
const encoder = new TextEncoder();
/**
 * In-memory registry of pending command status sequences.
 *
 * When a mutation handler (e.g. load-conn) fires, it calls
 * `scheduleCommandStatusStream` to register mock global_ids here.
 * When the frontend opens an EventSource to the SSE endpoint,
 * the handler looks up the ID, walks through a realistic status
 * progression, then closes the stream.
 */ const pendingCommands = new Map();
/**
 * Register global_ids so the SSE endpoint knows about them.
 * Called from IPsec mutation handlers before returning the response.
 */ export function scheduleCommandStatusStream(commands) {
    for (const cmd of commands){
        pendingCommands.set(cmd.globalId, {
            commandType: cmd.commandType
        });
    }
}
/** Format a single SSE named event. */ function sseEvent(eventName, data) {
    const json = JSON.stringify(data);
    return encoder.encode(`event: ${eventName}\ndata: ${json}\n\n`);
}
/**
 * MSW handler for the command-status-stream SSE endpoint.
 *
 * Simulates the real backend's status progression:
 *   pending -> in_progress -> succeeded
 *
 * Each transition is delayed by a randomized interval (300-2000ms) to
 * model independent worker nodes executing at different speeds — on a
 * real appliance the two sides of a tunnel are independent and report
 * back via separate status_push cycles.
 *
 * NOTE: The route uses a `*` prefix (e.g. `* /api/...`) so that MSW
 * matches regardless of the base URL origin. The strongswan handlers
 * omit the prefix because they are resolved through RTK Query's
 * `baseQuery`, which already provides the correct origin.
 */ export const commandStatusHandler = http.get("*/api/cluster/control-plane/command-status-stream/:global_id", ({ params })=>{
    const globalId = params.global_id;
    const entry = pendingCommands.get(globalId);
    // If the ID isn't registered, return a single "succeeded" event
    // (mimics the backend's "already terminal on connect" behavior).
    const commandType = entry?.commandType ?? "unknown";
    const statuses = [
        "pending",
        "in_progress",
        "succeeded"
    ];
    const stream = new ReadableStream({
        start (controller) {
            let idx = 0;
            function sendNext() {
                if (idx >= statuses.length) {
                    try {
                        controller.close();
                    } catch  {
                    // Stream may already be closed by the client.
                    }
                    return;
                }
                const status = statuses[idx];
                const update = {
                    global_id: globalId,
                    command_type: commandType,
                    status,
                    // Match the real backend's `skip_serializing_if = "Option::is_none"`:
                    // only include error_message when it has a value.
                    ...status === "failed" ? {
                        error_message: "Simulated failure"
                    } : {}
                };
                try {
                    controller.enqueue(sseEvent("status", update));
                } catch  {
                    // Client disconnected — stop sending.
                    return;
                }
                idx++;
                // Random delay between 300ms and 2000ms per step to simulate
                // independent worker nodes executing at different speeds.
                const delay = 300 + Math.random() * 1700;
                setTimeout(sendNext, delay);
            }
            // Initial delay before first event (simulates status_push interval)
            const initialDelay = 200 + Math.random() * 1000;
            setTimeout(sendNext, initialDelay);
            // Clean up the registry entry after the stream starts.
            if (entry) {
                pendingCommands.delete(globalId);
            }
        }
    });
    return new HttpResponse(stream, {
        headers: {
            "Content-Type": "text/event-stream",
            "Cache-Control": "no-cache",
            "X-Accel-Buffering": "no",
            Connection: "keep-alive"
        }
    });
});
export default commandStatusHandler;
